import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DoneIcon from '@mui/icons-material/Done';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import {
  IncomingPurchaseOrderDetail,
  confirmIncomingOrderDelivery,
  convertIncomingOrderToInvoice,
  getIncomingPurchaseOrderDetail,
  lateStockEntryForIncomingOrderItem,
  matchIncomingOrderItem,
  searchCatalogItemsForMatch,
} from '../../../services/ecommerceApi';
import accountingService from '../../../services/accountingService';

// ============================================================
// Gelen alış siparişi (B2B) detay dialogu.
// Kalem eşleme + geç stok girişi + alış faturasına çevirme.
// Hem B2B ekranında hem de Cari Siparişler listesinde yeniden kullanılır.
// ============================================================

export const STATUS_LABELS: Record<string, { label: string; color: 'default' | 'info' | 'warning' | 'success' | 'error' }> = {
  draft: { label: 'Taslak', color: 'default' },
  pending: { label: 'Beklemede', color: 'warning' },
  received: { label: 'Sipariş Alındı', color: 'info' },
  paid: { label: 'Ödendi', color: 'info' },
  confirmed: { label: 'Onaylandı', color: 'info' },
  processing: { label: 'Hazırlanıyor', color: 'info' },
  shipped: { label: 'Kargoda', color: 'info' },
  delivered: { label: 'Teslim Edildi', color: 'success' },
  completed: { label: 'Tamamlandı', color: 'success' },
  // İki yazım da desteklenir: canceled (ABD) / cancelled (İngiliz).
  canceled: { label: 'İptal', color: 'error' },
  cancelled: { label: 'İptal', color: 'error' },
};

export const formatMoney = (value: number, currency = 'TRY') =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'TRY' }).format(value || 0);

export const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

export const statusChip = (status: string) => {
  const meta = STATUS_LABELS[(status || '').toLowerCase()] || { label: status || '-', color: 'default' as const };
  return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />;
};

interface CatalogOption {
  id: string;
  name: string;
  stockCode?: string | null;
  barcode?: string | null;
  categoryName?: string | null;
}

interface WarehouseOption {
  id: string;
  name: string;
}

interface IncomingOrderDetailDialogProps {
  /** Açılacak gelen sipariş id'si (null → yükleme yapılmaz). */
  orderId: string | null;
  open: boolean;
  onClose: () => void;
  /** Eşleme/faturalaştırma/stok girişi sonrası çağrılır (parent listesini tazelemek için). */
  onChanged?: () => void;
  /** Şube id → görünen ad (opsiyonel; yoksa "Merkez"/id gösterilir). */
  branchLabel?: (branchId?: string | null) => string;
}

const IncomingOrderDetailDialog: React.FC<IncomingOrderDetailDialogProps> = ({
  orderId,
  open,
  onClose,
  onChanged,
  branchLabel,
}) => {
  const navigate = useNavigate();
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [detail, setDetail] = useState<IncomingPurchaseOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [unmatchedChoice, setUnmatchedChoice] = useState<'auto' | 'nostock' | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [receiptLines, setReceiptLines] = useState<Record<string, { received: number; damaged: number; reason: string }>>({});
  const [receiptNote, setReceiptNote] = useState('');
  const [receiptFullDelivery, setReceiptFullDelivery] = useState(true);
  const [tab, setTab] = useState(0);

  const showBranch = (id?: string | null) => (branchLabel ? branchLabel(id) : id || 'Merkez');

  const loadWarehouses = useCallback(async () => {
    try {
      const rows = await accountingService.getWarehouses(false);
      const mapped = (rows || [])
        .map((w: any) => ({
          id: String(w.id ?? w.Id ?? ''),
          name: String(w.name ?? w.Name ?? ''),
          isActive: w.isActive ?? w.IsActive ?? true,
          isDefault: w.isDefault ?? w.IsDefault ?? false,
        }))
        .filter((w) => w.id && w.isActive);
      setWarehouses(mapped.map(({ id, name }) => ({ id, name })));
      setSelectedWarehouseId((prev) => {
        if (prev && mapped.some((w) => w.id === prev)) return prev;
        const preferred = mapped.find((w) => w.isDefault) || mapped[0];
        return preferred?.id || '';
      });
    } catch {
      setWarehouses([]);
    }
  }, []);

  // orderId değişince (dialog açıkken) detayı yükle
  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    setDetail(null);
    setUnmatchedChoice(null);
    setDetailLoading(true);
    void loadWarehouses();
    (async () => {
      try {
        const res = await getIncomingPurchaseOrderDetail(orderId);
        if (!cancelled) {
          setDetail(res.data);
          initReceiptForm(res.data);
          setTab(pickDefaultTab(res.data));
        }
      } catch (err: any) {
        if (!cancelled) {
          setSnack({ message: err?.response?.data?.message || 'Sipariş detayı yüklenemedi.', severity: 'error' });
          onClose();
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId, loadWarehouses]);

  const pickDefaultTab = (d: IncomingPurchaseOrderDetail) => {
    const status = (d.status || '').toLowerCase();
    const shipped = !!(d.sellerWaybillNumber || '').trim()
      || ['shipped', 'delivered', 'completed'].includes(status);
    const receiptDone = !!(d.receiptStatus || d.receiptConfirmedAt);
    if (shipped && !receiptDone) return 1;
    if (!d.isInvoiced && shipped) return 2;
    return 0;
  };

  const initReceiptForm = (d: IncomingPurchaseOrderDetail) => {
    const init: Record<string, { received: number; damaged: number; reason: string }> = {};
    d.items.forEach((it) => {
      init[it.id] = {
        received: it.receivedQuantity ?? it.quantity,
        damaged: it.damagedQuantity ?? 0,
        reason: it.receiptReason ?? '',
      };
    });
    setReceiptLines(init);
    setReceiptNote(d.receiptNote ?? '');
    setReceiptFullDelivery(
      d.items.every((it) => (it.receivedQuantity ?? it.quantity) === it.quantity && !(it.damagedQuantity ?? 0))
    );
  };

  const refreshDetail = async () => {
    if (!detail) return;
    const res = await getIncomingPurchaseOrderDetail(detail.id);
    setDetail(res.data);
    initReceiptForm(res.data);
  };

  const applyFullDeliveryToLines = () => {
    if (!detail) return;
    const next: Record<string, { received: number; damaged: number; reason: string }> = {};
    detail.items.forEach((it) => {
      next[it.id] = { received: it.quantity, damaged: 0, reason: '' };
    });
    setReceiptLines(next);
    setReceiptFullDelivery(true);
  };

  const updateReceiptLine = (
    itemId: string,
    patch: Partial<{ received: number; damaged: number; reason: string }>,
    orderedQuantity: number
  ) => {
    if (!detail) return;
    setReceiptLines((prev) => {
      const merged = { received: orderedQuantity, damaged: 0, reason: '', ...prev[itemId], ...patch };
      const next = { ...prev, [itemId]: merged };
      const allFull = detail.items.every((it) => {
        const line = it.id === itemId ? merged : next[it.id] || { received: it.quantity, damaged: 0 };
        return Number(line.received) === it.quantity && Number(line.damaged) === 0;
      });
      setReceiptFullDelivery(allFull);
      return next;
    });
  };

  // Kalem eşleme (Autocomplete arama)
  const [matchOptions, setMatchOptions] = useState<CatalogOption[]>([]);
  const [matchSearchLoading, setMatchSearchLoading] = useState(false);
  const [matchingItemId, setMatchingItemId] = useState<string | null>(null);

  const searchProducts = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (term: string) => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!term || term.trim().length < 2) {
          setMatchOptions([]);
          return;
        }
        setMatchSearchLoading(true);
        try {
          setMatchOptions(await searchCatalogItemsForMatch(term));
        } catch {
          setMatchOptions([]);
        } finally {
          setMatchSearchLoading(false);
        }
      }, 350);
    };
  }, []);

  const handleMatch = async (itemId: string, inventoryItemId: string | null) => {
    if (!detail) return;
    setMatchingItemId(itemId);
    try {
      await matchIncomingOrderItem(detail.id, itemId, inventoryItemId);
      await refreshDetail();
      onChanged?.();
      setSnack({ message: inventoryItemId ? 'Kalem eşlendi.' : 'Eşleme kaldırıldı.', severity: 'success' });
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Eşleme güncellenemedi.', severity: 'error' });
    } finally {
      setMatchingItemId(null);
    }
  };

  const unmatchedItems = detail?.items.filter((i) => !i.matchedInventoryItemId) || [];

  // Sevkiyat kilidi: satıcı kargolamadan (shipped) faturalaştırma yapılamaz.
  // Kalem eşleştirme serbesttir; backend de aynı kuralı uygular (NOT_SHIPPED).
  const INVOICEABLE_STATUSES = ['shipped', 'delivered', 'completed'];
  const orderStatus = (detail?.status || '').toLowerCase();
  const isCancelled = orderStatus === 'cancelled';
  const hasSellerWaybill = !!(detail?.sellerWaybillNumber || '').trim();
  const awaitingShipment = !isCancelled && !hasSellerWaybill && !INVOICEABLE_STATUSES.includes(orderStatus);
  const alreadyReceipt = !!(detail?.receiptStatus || detail?.receiptConfirmedAt);
  const canConfirmDelivery = !isCancelled && !alreadyReceipt && (hasSellerWaybill || INVOICEABLE_STATUSES.includes(orderStatus));

  const openConvertDialog = () => {
    setUnmatchedChoice(null);
    setConvertDialogOpen(true);
    void loadWarehouses();
  };

  const handleConfirmDelivery = async () => {
    if (!detail) return;
    setConfirmingDelivery(true);
    try {
      const lines = detail.items.map((it) => {
        const line = receiptLines[it.id] || { received: it.quantity, damaged: 0, reason: '' };
        const received = Math.max(0, Math.min(Number(line.received) || 0, it.quantity));
        const damaged = Math.max(0, Math.min(Number(line.damaged) || 0, received));
        return {
          orderItemId: it.id,
          receivedQuantity: received,
          damagedQuantity: damaged,
          reason: line.reason?.trim() || null,
        };
      });
      const res = await confirmIncomingOrderDelivery(detail.id, {
        note: receiptNote.trim() || null,
        lines,
      });
      setSnack({ message: res.message || 'Teslim onaylandı.', severity: 'success' });
      await refreshDetail();
      onChanged?.();
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Teslim onayı kaydedilemedi.', severity: 'error' });
    } finally {
      setConfirmingDelivery(false);
    }
  };

  const handleConvert = async () => {
    if (!detail) return;
    if (unmatchedItems.length > 0 && !unmatchedChoice) return;
    setConverting(true);
    try {
      const res = await convertIncomingOrderToInvoice(detail.id, {
        autoCreateProducts: unmatchedChoice === 'auto',
        warehouseId: selectedWarehouseId || undefined,
      });
      setSnack({ message: res.message || 'Alış faturası oluşturuldu. Bu e-fatura değildir.', severity: 'success' });
      setConvertDialogOpen(false);
      setTab(2);
      await refreshDetail();
      onChanged?.();
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Faturalaştırma başarısız.', severity: 'error' });
    } finally {
      setConverting(false);
    }
  };

  const handleLateStockEntry = async (
    itemId: string,
    payload: { inventoryItemId?: string; autoCreate?: boolean }
  ) => {
    if (!detail) return;
    setMatchingItemId(itemId);
    try {
      const res = await lateStockEntryForIncomingOrderItem(detail.id, itemId, {
        ...payload,
        warehouseId: selectedWarehouseId || undefined,
      });
      setSnack({ message: res.message || 'Stok girişi yapıldı.', severity: 'success' });
      await refreshDetail();
      onChanged?.();
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Stok girişi yapılamadı.', severity: 'error' });
    } finally {
      setMatchingItemId(null);
    }
  };

  const renderCatalogCell = (item: IncomingPurchaseOrderDetail['items'][number]) => {
    if (!detail) return null;
    if (detail.isInvoiced) {
      if (item.matchedInventoryItemId) {
        return (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={item.matchedProductName || 'Eşleşti'}
          />
        );
      }
      return (
        <Stack spacing={1} sx={{ minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">Stok girilmedi</Typography>
          <Autocomplete<CatalogOption>
            size="small"
            options={matchOptions}
            loading={matchSearchLoading}
            disabled={matchingItemId === item.id}
            getOptionLabel={(opt) => `${opt.name}${opt.stockCode ? ` [${opt.stockCode}]` : ''}`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            filterOptions={(x) => x}
            noOptionsText="Ürün ara"
            onInputChange={(_, value) => searchProducts(value)}
            onChange={(_, value) => value && handleLateStockEntry(item.id, { inventoryItemId: value.id })}
            renderInput={(params) => (
              <TextField {...params} placeholder="Katalogdan seç" variant="outlined" />
            )}
          />
          <Button
            size="small"
            disabled={matchingItemId === item.id}
            onClick={() => {
              if (!window.confirm(`"${item.productName}" stoğa yeni ürün olarak eklensin mi?`)) return;
              void handleLateStockEntry(item.id, { autoCreate: true });
            }}
          >
            Yeni ürün aç
          </Button>
        </Stack>
      );
    }
    if (item.matchedInventoryItemId) {
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={item.matchedProductName || 'Eşleşti'}
          onDelete={matchingItemId === item.id ? undefined : () => handleMatch(item.id, null)}
        />
      );
    }
    return (
      <Autocomplete<CatalogOption>
        size="small"
        options={matchOptions}
        loading={matchSearchLoading}
        disabled={matchingItemId === item.id}
        getOptionLabel={(opt) => `${opt.name}${opt.stockCode ? ` [${opt.stockCode}]` : ''}`}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        filterOptions={(x) => x}
        noOptionsText="Ürün ara"
        onInputChange={(_, value) => searchProducts(value)}
        onChange={(_, value) => value && handleMatch(item.id, value.id)}
        renderInput={(params) => (
          <TextField {...params} placeholder="Katalogdan seç" variant="outlined" />
        )}
      />
    );
  };

  const goImportEInvoice = () => {
    if (!detail?.sellerEInvoiceETTN) return;
    onClose();
    navigate(
      `/fatura/alis/yeni?ettn=${encodeURIComponent(detail.sellerEInvoiceETTN)}&incomingPurchaseOrderId=${encodeURIComponent(detail.id)}`
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          Gelen sipariş
          {detail && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {detail.sellerCompanyName} · {detail.sellerOrderNumber} · {formatDate(detail.orderedAt)}
              {' · '}{showBranch(detail.branchId)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}
          {!detailLoading && detail && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {statusChip(detail.status)}
                <Chip
                  size="small"
                  variant="outlined"
                  color={alreadyReceipt ? (detail.hasReceiptDiscrepancy ? 'warning' : 'success') : 'default'}
                  label={alreadyReceipt ? (detail.hasReceiptDiscrepancy ? 'Teslim: eksik/hasar' : 'Teslim alındı') : 'Teslim bekliyor'}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color={detail.isInvoiced ? 'success' : 'default'}
                  label={detail.isInvoiced ? 'Fatura kesildi' : 'Fatura yok'}
                />
              </Stack>

              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab label="Sipariş" />
                <Tab label="Teslim" />
                <Tab label="Fatura" />
              </Tabs>

              {tab === 0 && (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Ürün</TableCell>
                          <TableCell align="right">Adet</TableCell>
                          <TableCell align="right">Tutar</TableCell>
                          <TableCell>Stoğumda</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2">{item.productName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[item.barcode, item.stockCode].filter(Boolean).join(' · ') || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.quantity} {item.unit}</TableCell>
                            <TableCell align="right">{formatMoney(item.totalPrice, detail.currency)}</TableCell>
                            <TableCell sx={{ minWidth: 200 }}>{renderCatalogCell(item)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Toplam {formatMoney(detail.totalAmount, detail.currency)}
                    </Typography>
                  </Box>
                  {detail.sellerWaybillNumber && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      İrsaliye {detail.sellerWaybillNumber}
                      {detail.trackingNumber ? ` · Kargo ${detail.trackingNumber}` : ''}
                    </Typography>
                  )}
                </>
              )}

              {tab === 1 && (
                <Stack spacing={2}>
                  {alreadyReceipt ? (
                    <Alert severity={detail.hasReceiptDiscrepancy ? 'warning' : 'success'} variant="outlined">
                      {detail.hasReceiptDiscrepancy
                        ? 'Teslim alındı, eksik veya hasar bildirildi.'
                        : 'Teslim alındı, her şey tamam.'}
                      {detail.receiptNote ? ` Not: ${detail.receiptNote}` : ''}
                    </Alert>
                  ) : canConfirmDelivery ? (
                    <Typography variant="body2" color="text.secondary">
                      Ürünleri kontrol edin. Eksik veya hasar varsa satırda yazın. Bu adım fatura kesmez.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Satıcı henüz sevk etmedi. Teslim onayı irsaliye geldikten sonra yapılır.
                    </Typography>
                  )}

                  {canConfirmDelivery && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={receiptFullDelivery}
                          onChange={(e) => {
                            if (e.target.checked) applyFullDeliveryToLines();
                            else setReceiptFullDelivery(false);
                          }}
                        />
                      }
                      label="Hepsini eksiksiz ve sağlam aldım"
                    />
                  )}

                  {(alreadyReceipt || canConfirmDelivery) && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Ürün</TableCell>
                            <TableCell align="right">Sipariş</TableCell>
                            <TableCell align="right">Gelen</TableCell>
                            <TableCell align="right">Hasarlı</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detail.items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.productName}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right" sx={{ width: 110 }}>
                                {canConfirmDelivery ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    inputProps={{ min: 0, max: item.quantity, step: 'any' }}
                                    value={receiptLines[item.id]?.received ?? item.quantity}
                                    onChange={(e) =>
                                      updateReceiptLine(item.id, { received: Number(e.target.value) }, item.quantity)
                                    }
                                    disabled={confirmingDelivery}
                                  />
                                ) : (
                                  item.receivedQuantity ?? item.quantity
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ width: 160 }}>
                                {canConfirmDelivery ? (
                                  <Stack spacing={1}>
                                    <TextField
                                      type="number"
                                      size="small"
                                      inputProps={{ min: 0, step: 'any' }}
                                      value={receiptLines[item.id]?.damaged ?? 0}
                                      onChange={(e) =>
                                        updateReceiptLine(item.id, { damaged: Number(e.target.value) }, item.quantity)
                                      }
                                      disabled={confirmingDelivery}
                                    />
                                    {!receiptFullDelivery && (
                                      <TextField
                                        size="small"
                                        value={receiptLines[item.id]?.reason ?? ''}
                                        onChange={(e) =>
                                          updateReceiptLine(item.id, { reason: e.target.value }, item.quantity)
                                        }
                                        placeholder="Sebep"
                                        disabled={confirmingDelivery}
                                      />
                                    )}
                                  </Stack>
                                ) : (
                                  item.damagedQuantity || 0
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {canConfirmDelivery && (
                    <TextField
                      multiline
                      minRows={2}
                      fullWidth
                      inputProps={{ maxLength: 2000 }}
                      label="Not (isteğe bağlı)"
                      value={receiptNote}
                      onChange={(e) => setReceiptNote(e.target.value)}
                      disabled={confirmingDelivery}
                    />
                  )}
                </Stack>
              )}

              {tab === 2 && (
                <Stack spacing={2}>
                  {detail.isInvoiced ? (
                    <Alert severity="success" variant="outlined">
                      Alış faturası kesildi{detail.invoicedAt ? ` · ${formatDate(detail.invoicedAt)}` : ''}.
                      Kasa ve banka değişmedi; tedarikçi carisine borç yazıldı.
                    </Alert>
                  ) : awaitingShipment ? (
                    <Typography variant="body2" color="text.secondary">
                      Satıcı henüz sevk etmedi. Fatura, irsaliye geldikten sonra kesilir.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      İki yol var. E-fatura resmi belgedir; kendi faturanız yalnızca muhasebe kaydıdır.
                    </Typography>
                  )}

                  {!detail.isInvoiced && !awaitingShipment && !isCancelled && (
                    <Stack spacing={1.5}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>E-fatura (önerilen)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Satıcı GİB’e e-fatura gönderir. Siz gelen belgeyi içeri alırsınız.
                          Kasa/banka değişmez.
                        </Typography>
                        {detail.sellerEInvoiceETTN ? (
                          <Button variant="contained" startIcon={<DownloadIcon />} onClick={goImportEInvoice}>
                            Gelen e-faturayı al
                          </Button>
                        ) : (
                          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => { onClose(); navigate('/e-fatura'); }}>
                            Gelen kutusuna bak
                          </Button>
                        )}
                        {!detail.sellerEInvoiceETTN && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Satıcı henüz e-fatura göndermedi.
                          </Typography>
                        )}
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Kendi alış faturamı kes</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          GİB’e gitmez. Sadece sizin panelinizde alış faturası oluşur.
                          E-fatura bunun yerine geçmez.
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={converting ? <CircularProgress size={16} /> : <ReceiptIcon />}
                          onClick={openConvertDialog}
                          disabled={converting}
                        >
                          Alış faturası kes
                        </Button>
                      </Paper>
                    </Stack>
                  )}

                  {detail.isInvoiced && !detail.sellerEInvoiceETTN && (
                    <Button size="small" onClick={() => { onClose(); navigate('/e-fatura'); }}>
                      E-fatura gelen kutusuna git
                    </Button>
                  )}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose}>Kapat</Button>
          {detail && tab === 1 && canConfirmDelivery && (
            <Button
              variant="contained"
              startIcon={confirmingDelivery ? <CircularProgress size={16} color="inherit" /> : <DoneIcon />}
              onClick={handleConfirmDelivery}
              disabled={confirmingDelivery}
            >
              Teslimi onayla
            </Button>
          )}
          {detail && tab === 2 && !detail.isInvoiced && !awaitingShipment && detail.sellerEInvoiceETTN && (
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={goImportEInvoice}>
              Gelen e-faturayı al
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={convertDialogOpen} onClose={() => !converting && setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Alış faturasına çevir</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                <strong>{detail.sellerOrderNumber}</strong> — {formatMoney(detail.totalAmount, detail.currency)}.
                Kendi muhasebenizde alış faturası kesilecek. E-fatura oluşmaz. Eşleşen ürünler stoğa girer, tedarikçi carisine borç yazılır. Kasa ve banka değişmez.
              </Typography>

              {warehouses.length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Depo</InputLabel>
                  <Select
                    value={selectedWarehouseId}
                    label="Depo"
                    onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  >
                    {warehouses.map((w) => (
                      <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {unmatchedItems.length === 0 ? (
                <Alert severity="success">Tüm kalemler katalogla eşleşti. Stok girişi yapılacak.</Alert>
              ) : (
                <>
                  <Alert severity="warning">
                    {unmatchedItems.length} kalem katalogla eşleşmedi. Devam etmeden bir seçenek işaretleyin:
                  </Alert>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {unmatchedItems.map((i) => (
                      <Typography key={i.id} component="li" variant="body2">
                        {i.productName} — {i.quantity} {i.unit}
                      </Typography>
                    ))}
                  </Box>
                  <RadioGroup
                    value={unmatchedChoice || ''}
                    onChange={(e) => setUnmatchedChoice(e.target.value as 'auto' | 'nostock')}
                  >
                    <FormControlLabel
                      value="auto"
                      control={<Radio />}
                      label="Eşleşmeyen kalemler için katalogda ürün oluştur ve stok girişi yap"
                    />
                    <FormControlLabel
                      value="nostock"
                      control={<Radio />}
                      label="Stoksuz devam et — kalemler faturaya manuel satır olarak girer"
                    />
                  </RadioGroup>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConvertDialogOpen(false)}
            disabled={converting}
          >
            {unmatchedItems.length > 0 ? 'Vazgeç ve kalemleri elle eşle' : 'Vazgeç'}
          </Button>
          <Button
            variant="contained"
            onClick={handleConvert}
            disabled={converting || (unmatchedItems.length > 0 && !unmatchedChoice)}
            startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
          >
            Faturalaştır
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} variant="filled">
          {snack?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default IncomingOrderDetailDialog;
